<script setup>
// BonBoZ: dải nhắc bật thông báo, mỏng, nằm trên cùng dashboard.
//
// Quyền thông báo chỉ xin được từ một cú bấm thật của người dùng — trình duyệt
// bỏ qua `requestPermission()` gọi lúc nạp trang hay lúc tin nhắn tới. Cú bấm
// đó có sẵn ở Cài đặt cá nhân > Thông báo, nhưng không ai tự vào đó, nên nhân
// viên ngồi cả ca mà chưa bao giờ nhận được thông báo nào.
//
// Dải này chỉ hiện đúng lúc còn có việc để làm: chưa hỏi bao giờ. Đã cấp hoặc
// đã từ chối thì `Notification.permission` khác 'default' và dải biến mất vĩnh
// viễn — không cần nhớ gì thêm. Quyền được đọc lại mỗi lần app quay lại màn
// hình, vì người dùng có thể đổi trong Cài đặt máy.
//
// Riêng iPhone mở trong tab Safari: không có `Notification` nên không xin được
// gì, việc cần làm là cài app ra màn hình chính. Dải hiện hướng dẫn đó, không
// có nút bật.
//
// Dải này chỉ hiện khi `usePushBanner` (helper/pushBanner.js) nhường chỗ cho
// nó: điện thoại đã nhận được thông báo và máy này không phải điện thoại đang
// bị chặn. Nhờ vậy điện thoại vừa bị chặn không loé lên câu "Bật thông báo…"
// trước khi banner sức khoẻ kịp hiện.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  isIOSBrowserTab,
  requestPushPermissions,
} from 'dashboard/helper/pushHelper';
import { onPageVisible } from 'dashboard/helper/pageVisibility';
import {
  devicePushState,
  refreshDevicePermission,
} from 'dashboard/helper/pushDeviceState';
import { syncAfterPushChange } from 'dashboard/helper/pushRecovery';
import { PUSH_BANNER, usePushBanner } from 'dashboard/helper/pushBanner';

// Ai bấm "Để sau" thì thôi không nhắc nữa trên máy này. Không có mốc này thì
// dải hiện lại mỗi lần nạp trang và biến thành thứ để người ta học cách phớt lờ.
const DISMISS_KEY = 'bonboz_bo_qua_nhac_thong_bao';

const wasDismissed = () => {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch (error) {
    // Trình duyệt chặn lưu trữ (cửa sổ riêng tư) — coi như chưa bỏ qua.
    return false;
  }
};

const needsIOSInstall = !('Notification' in window) && isIOSBrowserTab();

// Đọc quyền ngay lúc dựng, để lần vẽ đầu đã đúng.
refreshDevicePermission();
const banner = usePushBanner();
// Đã bấm "Bật thông báo" hoặc "Để sau" trong phiên này, hay "Để sau" từ trước.
const isClosed = ref(wasDismissed());

const isVisible = computed(
  () =>
    !isClosed.value &&
    banner.value.banner === PUSH_BANNER.PERMISSION &&
    (needsIOSInstall || devicePushState.value.permission === 'default')
);

let stopVisibilityListener = null;
onMounted(() => {
  stopVisibilityListener = onPageVisible(refreshDevicePermission);
});
onUnmounted(() => stopVisibilityListener?.());

// Xin quyền kèm đăng ký push trong một cú bấm. Lỗi (nếu có) do `pushHelper`
// tự báo; dải ẩn đi vì quyền đã khác 'default' hoặc người dùng đã đóng hộp
// thoại — nút ở Cài đặt cá nhân vẫn còn đó.
const enableNotifications = async () => {
  await requestPushPermissions();
  isClosed.value = true;
  await syncAfterPushChange();
};

const dismiss = () => {
  isClosed.value = true;
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch (error) {
    // Không nhớ được thì lần sau nhắc lại, chấp nhận được.
  }
};
</script>

<template>
  <!-- `v-show` chứ không `v-if`: đây là thẻ gốc của component, mà dải hết
       việc là trạng thái bình thường chứ không phải nhánh còn thiếu. -->
  <div
    v-show="isVisible"
    class="flex items-center justify-center gap-3 px-4 py-1.5 text-sm bg-n-brand/[0.12] text-n-slate-12"
  >
    <span
      class="i-lucide-bell shrink-0 size-4 text-n-brand"
      aria-hidden="true"
    />
    <span v-if="needsIOSInstall">
      {{ $t('BONBOZ.QUYEN_THONG_BAO.CAN_CAI_APP_IOS') }}
    </span>
    <span v-else>{{ $t('BONBOZ.QUYEN_THONG_BAO.DAI_NHAC') }}</span>
    <button
      v-if="!needsIOSInstall"
      class="px-2.5 py-1 font-medium rounded-lg bg-n-brand text-white hover:opacity-90"
      @click="enableNotifications"
    >
      {{ $t('BONBOZ.QUYEN_THONG_BAO.NUT') }}
    </button>
    <button
      class="px-2 py-1 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
      @click="dismiss"
    >
      {{ $t('BONBOZ.QUYEN_THONG_BAO.BO_QUA') }}
    </button>
  </div>
</template>
